import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import './App.css';
import { BACKEND_URL } from './config';
import Auth from './pages/Auth';
import Dashboard from './pages/Dashboard';
import ScanHistory from './pages/ScanHistory';
import Payment from './pages/Payment';
import LandingPage from './pages/LandingPage';
import PaymentSuccess from './pages/PaymentSuccess';
import Privacy from './pages/Privacy';
import PremiumFeatures from './pages/PremiumFeatures';
import AccountInfo from './pages/AccountInfo';
import AccountSettings from './pages/AccountSettings';
import Help from './pages/Help';
import FarmInsights from './pages/FarmInsights';
import Forum from './pages/Forum';
import AboutPage from './pages/AboutPage';
import OurProcessPage from './pages/OurProcessPage';
import WhyBoaiPage from './pages/WhyBoaiPage';
import AiSolutionsPage from './pages/AiSolutionsPage';
import FarmInsightsInfoPage from './pages/FarmInsightsInfoPage';
import CommunityInfoPage from './pages/CommunityInfoPage';
import MarketplaceInfoPage from './pages/MarketplaceInfoPage';
import ClimatePage from './pages/ClimatePage';
import VerifyProductPage from './pages/VerifyProductPage';
import AdminPanel from './pages/AdminPanel';
import OrderSuccessPage from './pages/OrderSuccessPage';

// Component to update page title based on route
function PageTitleUpdater() {
  const location = useLocation();
  
  useEffect(() => {
    const titles = {
      '/': 'BoAI - Intelligent Food Chains, Smarter Farms',
      '/auth': 'Login | BoAI',
      '/dashboard': 'Dashboard | BoAI',
      '/history': 'Scan History | BoAI',
      '/payment': 'Upgrade to Pro | BoAI',
      '/payment/success': 'Payment Success | BoAI',
      '/privacy': 'Privacy Policy | BoAI',
      '/about': 'About | BoAI',
      '/our-process': 'Our Process | BoAI',
      '/why-boai': 'Why BoAI | BoAI',
      '/ai-solutions': 'AI Solutions | BoAI',
      '/farm-insights-info': 'Farm Insights | BoAI',
      '/community-info': 'Community | BoAI',
      '/marketplace-info': 'Marketplace | BoAI',
      '/climate': 'Climate Solutions | BoAI'
    };
    
    document.title = titles[location.pathname] || 'BoAI - Intelligent Food Chains, Smarter Farms';
  }, [location]);
  
  return null;
}

function App() {
  const [token, setToken] = useState(sessionStorage.getItem('token'));
  const [user, setUser] = useState(null);
  const [language, setLanguage] = useState(localStorage.getItem('language') || 'en');

  const fetchUser = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setUser(data);
        setLanguage(data.language || 'en');
      } else {
        logout();
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Error fetching user:', error);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchUser();
    }
  }, [token, fetchUser]);

  const login = (newToken, userData) => {
    sessionStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    setLanguage(userData.language || 'en');
  };

  const logout = () => {
    sessionStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const toggleLanguage = () => {
    const newLang = language === 'en' ? 'hi' : 'en';
    setLanguage(newLang);
    localStorage.setItem('language', newLang);
  };

  return (
    <Router>
      <PageTitleUpdater />
      <div className="App">
        <Routes>
          <Route 
            path="/" 
            element={token ? <Navigate to="/dashboard" /> : <LandingPage onGetStarted={() => {}} />} 
          />
          <Route 
            path="/auth" 
            element={token ? <Navigate to="/dashboard" /> : <Auth onLogin={login} />} 
          />
          <Route 
            path="/dashboard" 
            element={
              token ? (
                <Dashboard 
                  user={user} 
                  token={token} 
                  language={language} 
                  onLogout={logout} 
                  onToggleLanguage={toggleLanguage}
                  onUserUpdate={fetchUser}
                />
              ) : (
                <Navigate to="/auth" />
              )
            } 
          />
          <Route 
            path="/farm-insights" 
            element={
              token ? (
                <FarmInsights 
                  user={user} 
                  token={token} 
                  language={language} 
                  onLogout={logout} 
                  onToggleLanguage={toggleLanguage}
                />
              ) : (
                <Navigate to="/auth" />
              )
            } 
          />
          <Route 
            path="/forum" 
            element={
              token ? (
                <Forum 
                  user={user} 
                  token={token} 
                  language={language} 
                />
              ) : (
                <Navigate to="/auth" />
              )
            } 
          />
          <Route 
            path="/forum/:topicId" 
            element={
              token ? (
                <Forum 
                  user={user} 
                  token={token} 
                  language={language} 
                />
              ) : (
                <Navigate to="/auth" />
              )
            } 
          />
          <Route 
            path="/history" 
            element={
              token ? (
                <ScanHistory 
                  token={token} 
                  language={language} 
                  onLogout={logout}
                  onToggleLanguage={toggleLanguage}
                />
              ) : (
                <Navigate to="/auth" />
              )
            } 
          />
          <Route 
            path="/payment" 
            element={
              token ? (
                <Payment 
                  user={user}
                  token={token} 
                  language={language} 
                  onLogout={logout}
                  onToggleLanguage={toggleLanguage}
                  onUpgradeSuccess={fetchUser}
                />
              ) : (
                <Navigate to="/auth" />
              )
            } 
          />
          <Route 
            path="/payment/success" 
            element={
              token ? (
                <PaymentSuccess 
                  token={token}
                  onUserUpdate={fetchUser}
                />
              ) : (
                <Navigate to="/auth" />
              )
            } 
          />
          <Route 
            path="/privacy" 
            element={<Privacy />} 
          />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/our-process" element={<OurProcessPage />} />
          <Route path="/why-boai" element={<WhyBoaiPage />} />
          <Route path="/ai-solutions" element={<AiSolutionsPage />} />
          <Route path="/farm-insights-info" element={<FarmInsightsInfoPage />} />
          <Route path="/community-info" element={<CommunityInfoPage />} />
          <Route path="/marketplace-info" element={<MarketplaceInfoPage />} />
          <Route path="/climate" element={<ClimatePage />} />
          <Route path="/verify/:productId" element={<VerifyProductPage />} />
          <Route path="/admin-panel" element={<AdminPanel />} />
          <Route path="/order-success" element={<OrderSuccessPage />} />
          <Route 
            path="/premium" 
            element={
              token ? (
                <PremiumFeatures 
                  user={user}
                  token={token} 
                  language={language} 
                  onLogout={logout}
                  onToggleLanguage={toggleLanguage}
                />
              ) : (
                <Navigate to="/auth" />
              )
            } 
          />
          <Route 
            path="/account" 
            element={
              token ? (
                <AccountInfo 
                  user={user}
                  token={token} 
                  language={language} 
                  onLogout={logout}
                  onToggleLanguage={toggleLanguage}
                />
              ) : (
                <Navigate to="/auth" />
              )
            } 
          />
          <Route 
            path="/settings" 
            element={
              token ? (
                <AccountSettings 
                  user={user}
                  token={token} 
                  language={language} 
                  onLogout={logout}
                  onToggleLanguage={toggleLanguage}
                  onUserUpdate={fetchUser}
                />
              ) : (
                <Navigate to="/auth" />
              )
            } 
          />
          <Route 
            path="/help" 
            element={
              token ? (
                <Help 
                  user={user}
                  token={token} 
                  language={language} 
                  onLogout={logout}
                  onToggleLanguage={toggleLanguage}
                />
              ) : (
                <Navigate to="/auth" />
              )
            } 
          />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
